import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import "./customColorPickerPopover.scss";
import { faPalette } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  COLOR_PICKER_POPUP_MARGIN,
  COLOR_PICKER_POPUP_TOTAL_HEIGHT,
  COLOR_PICKER_POPUP_WIDTH,
  ColorPickerPopup,
  IColorPickerPopupPosition,
} from "./popup/ColorPickerPopup";

interface ICustomColorPickerPopoverProps {
  title: string;
  hexColor: string;
  updateHexColor: (updatedHexColor: string | null) => void;
  portalParentId?: string;
  fullWidth?: boolean;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

interface ICustomColorPickerPopoverState {
  open: boolean;
  position: IColorPickerPopupPosition;
}

const initialPopupState: ICustomColorPickerPopoverState = {
  open: false,
  position: null,
};

export function CustomColorPickerPopover(props: ICustomColorPickerPopoverProps) {
  const { title, hexColor, updateHexColor, portalParentId, fullWidth = false, onGestureStart, onGestureEnd } =
    props;

  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorPickerPopupRef = useRef<HTMLDivElement>(null);

  const [popupState, setPopupState] = useState<ICustomColorPickerPopoverState>({ ...initialPopupState });

  function closePopup() {
    setPopupState({ ...initialPopupState });
  }

  function calculatePopupPosition(pickerRect: DOMRect) {
    const computedX = pickerRect.x + pickerRect.width / 2;
    let y = pickerRect.y - COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    if (y < 0) {
      y = pickerRect.y + pickerRect.height + COLOR_PICKER_POPUP_MARGIN;
    }

    const halfPopupWidth = COLOR_PICKER_POPUP_WIDTH / 2;
    const minX = halfPopupWidth + COLOR_PICKER_POPUP_MARGIN;
    const maxX = window.innerWidth - halfPopupWidth - COLOR_PICKER_POPUP_MARGIN;
    const x = Math.min(Math.max(computedX, minX), Math.max(minX, maxX));

    return { x, y };
  }

  const handlePointerDownOutsideColorPicker = useCallback((event: PointerEvent) => {
    if (
      colorPickerRef.current &&
      colorPickerPopupRef.current &&
      event.target instanceof Node &&
      !colorPickerRef.current.contains(event.target) &&
      !colorPickerPopupRef.current.contains(event.target)
    ) {
      closePopup();
    }
  }, []);

  useEffect(() => {
    if (popupState.open) {
      document.addEventListener("pointerdown", handlePointerDownOutsideColorPicker, { capture: true });
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutsideColorPicker, { capture: true });
    };
  }, [popupState.open, handlePointerDownOutsideColorPicker]);

  useEffect(() => {
    if (!popupState.open) {
      return;
    }

    function handleResizeWhileOpen() {
      const pickerRect = colorPickerRef.current?.getBoundingClientRect();
      if (!pickerRect) {
        return;
      }
      const { x, y } = calculatePopupPosition(pickerRect);
      setPopupState(prev => ({
        ...prev,
        position: { x, y },
      }));
    }

    window.addEventListener("resize", handleResizeWhileOpen);
    return () => window.removeEventListener("resize", handleResizeWhileOpen);
  }, [popupState.open, popupState.position]);

  function togglePopupState() {
    const colorPickerDimensions = colorPickerRef.current?.getBoundingClientRect();

    if (popupState.open || !colorPickerDimensions) {
      closePopup();
    } else {
      const { x, y } = calculatePopupPosition(colorPickerDimensions);
      setPopupState(prev => ({
        ...prev,
        open: true,
        position: {
          x,
          y,
        },
      }));
    }
  }

  return (
    <div
      ref={colorPickerRef}
      className={classNames("color-picker", {
        "color-picker-popup-open": popupState.open,
        "color-picker--full-width": fullWidth,
      })}
      onPointerDown={togglePopupState}
    >
      {popupState.open && (
        <ColorPickerPopup
          ref={colorPickerPopupRef}
          position={popupState.position}
          hexColor={hexColor}
          updateHexColor={updateHexColor}
          closePopup={closePopup}
          portalParentId={portalParentId}
          onGestureStart={onGestureStart}
          onGestureEnd={onGestureEnd}
        />
      )}
      <div className="color-picker-color-container">
        <div
          className="color-picker-color"
          style={{
            backgroundColor: hexColor,
          }}
        >
          {hexColor == null && <div className="color-picker-color-slash" />}
        </div>
        <div className="color-picker-title">{title}</div>
      </div>
      <FontAwesomeIcon className="color-picker-palette-icon" icon={faPalette} />
    </div>
  );
}
